/* ===========================================================================
   BKR — header solidity on scroll

   The header sits transparent over the hero photo, which is the point: the
   photo runs edge to edge behind it. But the theme's own test only turns the
   header solid once almost the whole hero has scrolled away, and on the way
   past, the hero's eyebrow slides directly behind the wordmark. Two pieces of
   type on top of each other, both white, for about half a second of scrolling.

   So the header goes solid on the first few pixels of movement instead. At
   rest it is still transparent over the photo; the moment you scroll, it is
   a solid bar and nothing can collide with it.

   This sets its own class rather than the theme's `is-solid`, so it never
   fights the hero observer in bkr.js — both produce the same look, and
   whichever is on wins. Scrolling back to the very top clears it.
   ======================================================================== */
(function () {
  'use strict';

  var stack = document.getElementById('topstack');
  if (!stack) return;

  /* Small enough that the bar is solid before any hero type reaches it. */
  var THRESHOLD = 16;
  var queued = false;

  function apply() {
    queued = false;
    stack.classList.toggle('is-scrolled', window.scrollY > THRESHOLD);
  }

  function onScroll() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(apply);
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  /* Reloading part-way down a page should start solid, not transparent. */
  apply();
})();


/* ===========================================================================
   BKR — pickup location

   One choice per order, made at the end of the cart. Two jobs:

   1. Save it the moment it changes, as a cart attribute. The drawer gets
      re-rendered every time a quantity changes, so relying on the form
      submit alone would lose the choice. Saving on change also keeps the
      cart page and the drawer in step when both are on screen.

   2. Refuse to check out without one. The cart page form carries novalidate,
      so the browser's own `required` never fires there — this does it.
   ======================================================================== */
(function () {
  'use strict';

  var SEL = '[data-pickup-attr]';
  var ATTR = 'Pickup location';

  function updateUrl() {
    var r = window.BKR && window.BKR.routes;
    return (r && r.cart_update) ? r.cart_update + '.js' : '/cart/update.js';
  }

  function fieldOf(el) { return el.closest ? el.closest('[data-pickup-field]') : null; }

  function clearError(el) {
    var field = fieldOf(el);
    if (!field) return;
    field.classList.remove('pickup--bad');
    var msg = field.querySelector('[data-pickup-error]');
    if (msg) msg.hidden = true;
  }

  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || !sel.matches || !sel.matches(SEL)) return;

    clearError(sel);

    /* Keep every copy of the picker showing the same answer. */
    var all = document.querySelectorAll(SEL);
    for (var i = 0; i < all.length; i++) {
      if (all[i] !== sel) all[i].value = sel.value;
    }

    var body = { attributes: {} };
    body.attributes[ATTR] = sel.value;

    fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).catch(function () {
      /* Saving failed, but the value still rides along on form submit. */
    });
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    var sel = form.querySelector(SEL);
    if (!sel || sel.value) return;

    e.preventDefault();
    var field = fieldOf(sel);
    if (field) {
      field.classList.add('pickup--bad');
      var msg = field.querySelector('[data-pickup-error]');
      if (msg) msg.hidden = false;
    }
    if (sel.scrollIntoView) sel.scrollIntoView({ block: 'center', behavior: 'smooth' });
    sel.focus();
  });
})();


/* ===========================================================================
   BKR — empty the bag

   One button for "start again", rather than removing lines one at a time.
   It reloads afterwards instead of re-rendering: everything on the page
   that knows about the cart — the drawer, the counter, the cart page —
   comes back correct, and there is nothing left half-updated.
   ======================================================================== */
(function () {
  'use strict';

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-cart-clear]') : null;
    if (!btn) return;

    e.preventDefault();
    if (btn.dataset.busy) return;
    btn.dataset.busy = '1';

    var base = (window.BKR && window.BKR.routes && window.BKR.routes.cart) || '/cart';
    fetch(base + '/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}'
    }).then(function () {
      window.location.reload();
    }).catch(function () {
      window.location.reload();
    });
  });
})();


/* ===========================================================================
   BKR — the inclusion upcharge

   A plain boule is $12; the same loaf with an inclusion is $15. The bake day
   is the variant, so its price can't change with a line-item choice — and it
   shouldn't, because one bake day has to keep one honest stock count. Six
   loaves are six loaves however they're flavoured.

   So the $3 difference rides along as its own cart line, tied to a hidden
   product. This layer keeps that line in step with the cart:

   - adding an item that carries a chargeable choice sends the upcharge in
     the SAME request, so the drawer that comes back already shows both;
   - changing or removing a line trues the upcharge up afterwards, and hands
     the corrected response back to the theme, so what gets rendered is the
     cart that actually exists.

   Configured from Theme settings > Cart. With no variant set it does
   nothing at all, which is what a store without an upcharge wants.
   ======================================================================== */
(function () {
  'use strict';

  var conf = (window.BKR && window.BKR.addon) || null;
  if (!conf || !Number(conf.variantId)) return;

  var VARIANT = String(Number(conf.variantId));
  var PROP = conf.property || 'Inclusions';
  var routes = (window.BKR && window.BKR.routes) || {};
  var ADD = routes.cart_add || '/cart/add';
  var CHANGE = routes.cart_change || '/cart/change';
  var UPDATE = routes.cart_update || '/cart/update';

  /* Choices that cost nothing. "Plain" is the $12 loaf. */
  var FREE = String(conf.free || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);

  function path(u) {
    try { return new URL(u, window.location.origin).pathname; }
    catch (e) { return String(u); }
  }
  function hits(u, route) { return path(u).indexOf(path(route)) === 0; }

  /* One chosen value: does it cost extra? */
  function charges(value) {
    var v = String(value == null ? '' : value).trim();
    if (!v) return false;
    return FREE.indexOf(v.toLowerCase()) === -1;
  }

  /* A cart line is chargeable when its chosen value costs extra. */
  function chargeable(props) {
    return !!(props && charges(props[PROP]));
  }

  function wanted(cart) {
    var n = 0;
    (cart.items || []).forEach(function (it) {
      if (String(it.variant_id) === VARIANT) return;
      if (chargeable(it.properties)) n += it.quantity;
    });
    return n;
  }

  function present(cart) {
    var n = 0;
    (cart.items || []).forEach(function (it) {
      if (String(it.variant_id) === VARIANT) n += it.quantity;
    });
    return n;
  }

  var native = window.fetch.bind(window);

  /* The bag counter should say how many things the customer bought, not
     how many lines the cart happens to hold. Two loaves are two loaves,
     even though a third line is carrying their $3. */
  var CART = routes.cart || '/cart';
  function isCartJson(u) { return path(u) === path(CART) + '.js'; }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isPost = init && init.body && String(init.method || '').toUpperCase() === 'POST';

    if (!isPost && isCartJson(url)) {
      return native(input, init).then(function (res) {
        if (!res.ok) return res;
        return res.clone().json().then(function (cart) {
          var hidden = present(cart);
          if (!hidden) return res;
          cart.item_count = Math.max(0, (cart.item_count || 0) - hidden);
          return new Response(JSON.stringify(cart), {
            status: res.status,
            headers: { 'Content-Type': 'application/json' }
          });
        }).catch(function () { return res; });
      });
    }

    if (isPost && hits(url, ADD)) {
      try {
        var body = JSON.parse(init.body);
        var extra = [];
        (body.items || []).forEach(function (line) {
          if (chargeable(line.properties)) {
            extra.push({ id: Number(VARIANT), quantity: Number(line.quantity) || 1 });
          }
        });
        if (extra.length) {
          body.items = body.items.concat(extra);
          init = Object.assign({}, init, { body: JSON.stringify(body) });
        }
      } catch (e) { /* not our JSON — send it untouched */ }
      return native(input, init);
    }

    if (isPost && (hits(url, CHANGE) || hits(url, UPDATE))) {
      return native(input, init).then(function (res) {
        if (!res.ok) return res;
        return res.clone().json().then(function (cart) {
          var want = wanted(cart);
          if (want === present(cart)) return res;

          /* Raising or lowering an existing line is an update. Putting the
             line BACK after it reached zero is an add — /cart/update.js
             cannot resurrect a line that is no longer in the cart, and
             asking it to is what used to throw. */
          var adding = present(cart) === 0;
          var payload;
          if (adding) {
            payload = { items: [{ id: Number(VARIANT), quantity: want }] };
          } else {
            payload = { updates: {} };
            payload.updates[VARIANT] = want;
          }

          try {
            var sent = JSON.parse(init.body);
            /* Ask for the same sections the theme asked for, so it can
               render the corrected cart with its own code. */
            if (sent.sections) payload.sections = sent.sections;
            if (sent.sections_url) payload.sections_url = sent.sections_url;
          } catch (e) { /* no sections — the correction still lands */ }

          return native((adding ? ADD : UPDATE) + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload)
          }).then(function (fixed) { return fixed.ok ? fixed : res; });
        }).catch(function () { return res; });
      });
    }

    return native(input, init);
  };

  /* ---------------------------------------------------------------------
     On the product page, say so before the cart does. The note is built
     here rather than in the template, so the whole upcharge feature lives
     in one file and one settings group.
     ------------------------------------------------------------------ */
  var NOTE = String(conf.note || '').trim();

  function noteFor(field) {
    var box = field.closest ? field.closest('.choices') : null;
    if (!box) return null;
    var p = box.querySelector('[data-addon-note]');
    if (!p) {
      p = document.createElement('p');
      p.className = 'addon-note';
      p.setAttribute('data-addon-note', '');
      p.textContent = NOTE;
      p.hidden = true;
      box.appendChild(p);
    }
    return p;
  }

  function syncNote() {
    if (!NOTE) return;
    var fields = document.querySelectorAll('[data-prop]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      if (el.dataset.prop !== PROP) continue;
      var p = noteFor(el);
      if (!p) continue;
      var v = el.type === 'checkbox' ? (el.checked ? el.value : '') : el.value;
      p.hidden = !charges(v);
    }
  }

  document.addEventListener('change', function (e) {
    if (e.target && e.target.dataset && e.target.dataset.prop === PROP) syncNote();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncNote);
  } else {
    syncNote();
  }
})();
